import { generatePath } from 'react-router-dom'
import { Badge, CompanyTag } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { GridCellProps, GridColumnProps } from '@progress/kendo-react-grid'
import { GroupReferenceDto, GroupReferenceType } from 'api/Common/Types/GroupReferenceDto'
import { TicketDto, TicketKey } from 'api/Tickets/Types/TicketDto'
import classNames from 'classnames'
import { HeaderSortCell } from 'components/Filter'
import { WarningOutlined } from 'components/Icon'
import { Link } from 'components/Link'
import { formatLocalDateUTC, useDifferenceInFullDays } from 'utils/date'
import { links, ticketDetailsRouteUrl } from 'utils/featureToggles'

import { generateClassNameForReverseType } from '../../helpers/transactions'
import MLRuns from './MLRuns/MLRuns'
import TransactionValidations, {
  TransactionValidationType,
} from './TransactionValidations/TransactionValidations'

export interface GridColumn extends GridColumnProps {
  show: boolean
  width?: number
  allowNarrowWidth?: boolean
}

const mlStatusBadgeVariant = (name: string): 'green' | 'red' | 'orange' | 'neutral' => {
  switch (name) {
    case 'Guardrail Blocked':
    case 'Approved':
      return 'green'
    case 'Manual Review':
    case 'Pending':
      return 'orange'
    default:
      return 'neutral'
  }
}

export const ticketsPaymentsColumn: GridColumn[] = [
  {
    title: 'Number',
    field: TicketKey.NUMBER,
    cell: (props: GridCellProps) => (
      <td style={{ color: 'var(--link)', textDecoration: 'underline' }}>
        <Link
          to={{
            pathname: ticketDetailsRouteUrl(props.dataItem.id, props.dataItem.viewType.id),
          }}
        >
          {props.dataItem.number}
        </Link>
      </td>
    ),
    show: true,
    filterable: true,
    sortable: false,
    width: 95,
    filter: 'text',
  },
  {
    title: 'Type',
    field: TicketKey.TYPE,
    show: true,
    filterable: true,
    sortable: false,
    className: 'table-columns__content',
    filter: 'text',
  },
  {
    title: 'Group Reference',
    field: TicketKey.GROUP_REFERENCE,
    cell: (props: GridCellProps): JSX.Element => {
      if (!props.dataItem.data?.transaction) {
        return <td />
      }
      if (!props.dataItem.data?.transaction?.groupReferences) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }
      return (
        <td className={generateClassNameForReverseType(props.dataItem)}>
          {props.dataItem.data.transaction.groupReferences.map((groupRef: GroupReferenceDto) => {
            const groupLink: { pathname?: string; search?: string } = {
              pathname: undefined,
              search: undefined,
            }
            const isActive = groupRef.type.id === GroupReferenceType.WithdrawBulk
            if (groupRef.type.id === GroupReferenceType.WithdrawBulk) {
              const urlSearchParams = new URLSearchParams()
              urlSearchParams.set('id', groupRef.id)
              groupLink.pathname = links.transactionBulkWithdrawals.url
              groupLink.search = `?${urlSearchParams.toString()}`
            }
            return (
              <Link to={groupLink}>
                <Tooltip>
                  <TooltipTrigger render={<span className='inline-flex' />}>
                    <Badge
                      className={classNames({ 'has-pointer': isActive })}
                      variant={isActive ? 'info' : 'neutral'}
                    >
                      {groupRef.name}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>{groupRef.type.name}</TooltipContent>
                </Tooltip>
              </Link>
            )
          })}
        </td>
      )
    },
    show: true,
    filter: 'text',
    filterable: true,
    sortable: false,
    minResizableWidth: 80,
    width: 80,
  },
  {
    title: 'Bulk Process Eligible',
    field: TicketKey.BULK_PROCESS_ELIGIBLE,
    width: 80,
    allowNarrowWidth: true,
    cell: (props: GridCellProps): JSX.Element => {
      if (!props.dataItem.data?.transaction) {
        return <td />
      }
      if (!Object.keys(props.dataItem.data.transaction).includes('canBulkProcess')) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }
      return (
        <td>
          {props.dataItem.data.transaction.canBulkProcess ? (
            <TransactionValidations
              type={TransactionValidationType.Yes}
              transactionId={props.dataItem.data.transaction.id}
            />
          ) : (
            // <Tag color="green">Yes</Tag>
            <TransactionValidations
              type={TransactionValidationType.No}
              transactionId={props.dataItem.data.transaction.id}
            />
          )}
        </td>
      )
    },
    show: true,
    filterable: true,
    sortable: false,
    className: 'table-columns__content',
    filter: 'text',
  },
  {
    title: 'ML Status',
    field: TicketKey.ML_STATUS,
    width: 140,
    cell: (props: GridCellProps): JSX.Element => {
      if (!props.dataItem.data?.transaction) {
        return <td />
      }
      const mlStatus = props.dataItem.data.transaction.mlStatus
      if (!mlStatus) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }
      return (
        <td className={generateClassNameForReverseType(props.dataItem)}>
          <MLRuns
            masterTransactionId={props.dataItem.data.transaction.id}
            mlStatus={mlStatus}
            badgeVariant={mlStatusBadgeVariant(mlStatus.name)}
          />
        </td>
      )
    },
    show: true,
    filterable: true,
    sortable: false,
    className: 'table-columns__content',
    filter: 'text',
  },
  {
    title: 'Reference',
    field: TicketKey.REFERENCE,
    cell: (props: GridCellProps): JSX.Element => {
      if (!props.dataItem.data?.transaction) {
        return <td>{props.dataItem.referenceId}</td>
      }
      return (
        <td style={{ color: 'var(--link)', textDecoration: 'underline' }}>
          <Link
            to={{
              pathname: generatePath('/transactions/:id', {
                id: props.dataItem.data.transaction.id,
              }),
            }}
          >
            {props.dataItem.referenceId}
          </Link>
        </td>
      )
    },
    show: true,
    filterable: true,
    sortable: false,
    className: 'table-columns__content',
    filter: 'text',
  },
  {
    title: 'Description',
    field: TicketKey.DESCRIPTION,
    cell: (props: GridCellProps): JSX.Element => {
      return (
        <td className='columns__content'>
          <div>
            {props.dataItem.description}{' '}
            {props.dataItem.exclamationMarkIcon && (
              <span style={{ color: 'red' }}>
                <WarningOutlined />
              </span>
            )}
          </div>
        </td>
      )
    },
    show: true,
    filterable: true,
    sortable: false,
    className: 'table-columns__content',
    filter: 'text',
  },
  {
    title: 'Client',
    field: TicketKey.CLIENT,
    cell: (props: GridCellProps): JSX.Element => {
      if (!props.dataItem.client) {
        return <td />
      }
      return (
        <td
          className='table-columns__content'
          style={{ color: 'var(--link)', textDecoration: 'underline' }}
        >
          <Link
            to={{
              pathname: generatePath(links.recordsClients.detailsUrl as string, {
                id: props.dataItem.client.id,
                slug: 'details',
              }),
            }}
          >
            {props.dataItem.client.name}
          </Link>
        </td>
      )
    },
    show: true,
    filterable: true,
    sortable: false,
    filter: 'text',
  },
  {
    title: 'Country',
    field: TicketKey.COUNTRY,
    cell: (props: GridCellProps): JSX.Element => {
      const isSalesTicketType = props.dataItem.viewType.id === 3
      const data: TicketDto = props.dataItem
      if (isSalesTicketType) {
        if (data.client) {
          return <td>{data.client?.country?.name}</td>
        }
        if (!data.lead?.country.id) {
          return <td />
        }
        return <td>{data.lead?.country.name}</td>
      }
      if (!data.client?.country?.id) {
        return <td />
      }
      return <td>{props.dataItem.client.country.name}</td>
    },
    show: true,
    filterable: true,
    sortable: false,
    className: 'table-columns__content',
    filter: 'text',
  },
  {
    title: 'Status',
    field: TicketKey.STATUS,
    show: true,
    filterable: true,
    sortable: false,
    className: 'table-columns__content',
    filter: 'text',
  },
  {
    title: 'Transaction Status',
    field: TicketKey.TRANSACTION_STATUS,
    show: true,
    filterable: true,
    sortable: false,
    className: 'table-columns__content',
    filter: 'text',
  },
  {
    title: 'Assignee',
    field: TicketKey.ASSIGNEE,
    show: true,
    filterable: true,
    sortable: false,
    className: 'table-columns__content',
    filter: 'text',
  },
  {
    title: 'Entity',
    field: TicketKey.TICKMILL_COMPANY,

    cell: (props: GridCellProps): JSX.Element => {
      const isSalesTicketType = props.dataItem.viewType.id === 3
      if (isSalesTicketType) {
        if (!props.dataItem.lead?.tickmillCompany?.id) {
          return <td />
        }
        return (
          <td>
            <CompanyTag id={props.dataItem.lead.tickmillCompany.id}>
              {props.dataItem.lead.tickmillCompany.name}
            </CompanyTag>
          </td>
        )
      }
      if (!props.dataItem.client?.tickmillCompany?.id) {
        return <td />
      }
      return (
        <td>
          <CompanyTag id={props.dataItem.client.tickmillCompany.id}>
            {props.dataItem.client.tickmillCompany.name}
          </CompanyTag>
        </td>
      )
    },
    show: true,
    sortable: false,
    filter: 'text',
    width: 80,
    allowNarrowWidth: true,
    minResizableWidth: 80,
  },
  {
    title: 'Date Created',
    field: TicketKey.DATE_CREATED,
    headerCell: HeaderSortCell,
    cell: (props: GridCellProps) => (
      <td className='table-columns__date'>
        {formatLocalDateUTC(props.dataItem.createDate, true)}
        <br />
        {useDifferenceInFullDays(props.dataItem.createDate)}
      </td>
    ),
    show: true,
    filterable: true,
    sortable: true,
    width: 150,
    filter: 'text',
  },
  {
    title: 'Date Updated',
    field: TicketKey.DATE_UPDATED,
    headerCell: HeaderSortCell,
    cell: (props: GridCellProps) => (
      <td className='table-columns__date'>
        {props.dataItem.updateDate && (
          <>
            {formatLocalDateUTC(props.dataItem.updateDate, true)}
            <br />
            {useDifferenceInFullDays(props.dataItem.updateDate)}
          </>
        )}
      </td>
    ),
    show: true,
    filterable: true,
    sortable: true,
    width: 150,
    filter: 'text',
  },
]
